'use strict'

import { AppContext } from "@lightspeed-invoicing/core-client/frontend";
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { GlobalHelper } from '../indexImports.js';
import { INTEGRATION_KEY, INTEGRATION_LABEL } from "../state/integration/Constants";
import { details, integrationKeyQuickbooks } from "./IntegrationConstants.jsx";

const shopifyExternalUrl = "https://apps.shopify.com/quotemachine"

class Integrations {

    static getPaymentTransactionExternalUrl(externalLink) {
        let url = ''
        if (!externalLink || !externalLink.attributes.integrationKey) {
            return ''
        }

        switch (externalLink.attributes.integrationKey) {
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                url = 'https://us.merchantos.com/?name=transaction.views.transaction&form_name=view&tab=details&id=' + externalLink.attributes.externalId
                break
            case INTEGRATION_KEY.LIGHTSPEED_ECOM:
            case INTEGRATION_KEY.LIGHTSPEED_ECOM_OMNI:
            case INTEGRATION_KEY.LIGHTSPEED_ECOM_OMNI_NA:
                const lsEcomIntegration = AppContext.integrations.find(i => i.attributes.integrationKey === 'ls-ecom' || i.attributes.integrationKey === 'ls-ecom-omni' || i.attributes.integrationKey === 'ls-ecom-omni-na')
                if (lsEcomIntegration) {
                    url = `https://${lsEcomIntegration.attributes.configuration.mainDomain}/admin/orders/${externalLink.attributes.externalId}`
                }
                break

            case INTEGRATION_KEY.SHOPIFY:
            case INTEGRATION_KEY.SHOPIFY_EXPERT_APP:
                let externalAccountId = AppContext.getIntegrationExternalAccountId(externalLink.attributes.integrationKey)
                url = `https://${externalAccountId}/admin/orders/${externalLink.attributes.externalId}`
                break
            case INTEGRATION_KEY.QUICK_BOOKS:
                url = 'https://sandbox.qbo.intuit.com/app/recvpayment?txnId='
                if (AppContext.getOptions().environment === "prod") {
                    url = 'https://qbo.intuit.com/app/recvpayment?txnId='
                }
                url = url + externalLink.attributes.externalId
                break
            case INTEGRATION_KEY.VEND:
                let accountId = AppContext.getIntegrationExternalAccountId(INTEGRATION_KEY.VEND)
                url = `https://${accountId}.retail.lightspeed.app/sales/all?ids=${externalLink.attributes.externalId}`
                break
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                url = `https://squareupsandbox.com/dashboard/sales/transactions/${externalLink.attributes.externalId}`
                if (AppContext.getOptions().environment === "prod") {
                    url = `https://squareup.com/dashboard/sales/transactions/${externalLink.attributes.externalId}`
                }
                break
            case INTEGRATION_KEY.WIX:
                const wixIntegration = GlobalHelper.getIntegration(INTEGRATION_KEY.WIX)
                if (wixIntegration) {
                    url = `https://manage.wix.com/dashboard/${wixIntegration.attributes.configuration.shopId}/payments-dashboard/payment/${externalLink.attributes.externalId}`
                }
                break
            case INTEGRATION_KEY.FRESHBOOKS:
                return this.getExternalOrderUrl(externalLink)
                break
        }
        return url
    }

    static getIntegratedPaymentTransactionExternalUrl(externalLink) {
        let url = ''
        if (!externalLink || !externalLink.attributes.integrationKey) {
            return ''
        }

        switch (externalLink.attributes.integrationKey) {
            case INTEGRATION_KEY.STRIPE:
                url = 'https://dashboard.stripe.com/payments/' + externalLink.attributes.externalId
                break
            case INTEGRATION_KEY.SHOPIFY:
            case INTEGRATION_KEY.SHOPIFY_EXPERT_APP:
                let shopDomain = AppContext.getIntegrationExternalAccountId(externalLink.attributes.integrationKey)
                url = `https://${shopDomain}/admin/orders/${externalLink.attributes.externalId}`
                break
            case INTEGRATION_KEY.PAYSAFE:
                url = "https://login.netbanx.com/office/reports/showActivity.htm"
                break
            case INTEGRATION_KEY.VEND:
                let accountId = AppContext.getIntegrationExternalAccountId(INTEGRATION_KEY.VEND)
                url = `https://${accountId}.retail.lightspeed.app/sales/all?ids=${externalLink.attributes.externalId}`
                break
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                url = `https://squareupsandbox.com/dashboard/sales/transactions/${externalLink.attributes.externalId}`
                if (GlobalHelper.isEnvProduction()) {
                    url = `https://squareup.com/dashboard/sales/transactions/${externalLink.attributes.externalId}`
                }
                break
        }
        return url
    }

    static getPaymentExportedFormattedMessage(externalLink) {
        if (!externalLink || !externalLink.attributes.integrationKey) {
            return ''
        }
        const integrationKey = externalLink.attributes.integrationKey
        switch (integrationKey) {
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                return <FormattedMessage
                    id="integration.integrations.payment_exported_as_credit_deposit"
                    defaultMessage="This payment has been exported to {integrationKey} as a credit account deposit"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            case INTEGRATION_KEY.QUICK_BOOKS:
            case INTEGRATION_KEY.FRESHBOOKS:
                return <FormattedMessage
                    id="integration.integrations.payment_exported_as_invoice_payment"
                    defaultMessage="This payment has been exported to {integrationKey} as an invoice payment"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}

                />
            case INTEGRATION_KEY.SHOPIFY:
            case INTEGRATION_KEY.SHOPIFY_EXPERT_APP:
            case INTEGRATION_KEY.VEND:
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                return <FormattedMessage
                    id="integration.integrations.payment_exported"
                    defaultMessage="This payment has been exported to {integrationKey}"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            default:
                return ''
        }
    }

    static getPaymentExportedCtaFormattedMessage(externalLink) {
        if (!externalLink || !externalLink.attributes.integrationKey || externalLink.attributes.externalId === "non-applicable") {
            return ''
        }

        const integrationKey = externalLink.attributes.integrationKey
        switch (integrationKey) {
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                return <FormattedMessage
                    id="integration.integrations.open_credit_account"
                    defaultMessage="Open credit account deposit in {integrationKey}"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            case INTEGRATION_KEY.SHOPIFY:
            case INTEGRATION_KEY.SHOPIFY_EXPERT_APP:
            case INTEGRATION_KEY.QUICK_BOOKS:
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
            case INTEGRATION_KEY.WIX:
            case INTEGRATION_KEY.FRESHBOOKS:
                return <FormattedMessage
                    id="integration.integrations.open_payment_in"
                    defaultMessage="Open payment in {integrationKey}"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            case INTEGRATION_KEY.STRIPE:
            case INTEGRATION_KEY.PAYSAFE:
                return <FormattedMessage
                    id="integration.integrations.open_payment_from"
                    defaultMessage="Open payment from {integrationKey}"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            case INTEGRATION_KEY.VEND:
                return <FormattedMessage
                    id="integration.integrations.open_sales_listing"
                    defaultMessage="Open sales listing in {integrationKey}"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            default:
                return ''
        }
    }

    static getPaymentExportedLogoImg(externalLink) {
        if (!externalLink || !externalLink.attributes.integrationKey) {
            return ''
        }

        switch (externalLink.attributes.integrationKey) {
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                return <img src="/images/lightspeed_retail_logo.png" width="100px" />
            case INTEGRATION_KEY.QUICK_BOOKS:
                return <img src="/images/quickbooks_logo.png" width="130px" />
            case INTEGRATION_KEY.SHOPIFY:
            case INTEGRATION_KEY.SHOPIFY_EXPERT_APP:
                return <img src="/images/shopify_logo.png" width="75px" />
            case INTEGRATION_KEY.STRIPE:
                return <img src={"/images/stripe_logo.png"} width="80px" />
            case INTEGRATION_KEY.PAYSAFE:
                return <img src={"/images/paysafe_logo.jpg"} width="80px" />
            case INTEGRATION_KEY.VEND:
                return <img src={"/images/lightspeed_logo.png"} width="120px" />
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                return <img src={"/images/square_logo.png"} width="70px" />
            case INTEGRATION_KEY.FRESHBOOKS:
                return <img src={"/images/freshbooks_logo.png"} width="100px" />
            case INTEGRATION_KEY.WIX:
                return <img src={"/images/wix_logo.png"} width="70px" />
            default:
                return ''
        }
    }

    static getPaymentTransactionMessage(integrationKey) {
        switch (integrationKey) {
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
            case INTEGRATION_KEY.QUICK_BOOKS:
                return <FormattedMessage
                    id="integration.integrations.credit_memo_exported_as_credit_deposit"
                    defaultMessage="This credit memo has been exported to {integrationKey} as a credit account deposit"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            case INTEGRATION_KEY.STRIPE:
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                return <FormattedMessage
                    id="integration.integrations.payment_made_with"
                    defaultMessage="This payment was made with {integrationKey}"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            default:
                return ''
        }
    }

    static getPaymentTransactionButtonLabel(integrationKey) {
        switch (integrationKey) {
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
            case INTEGRATION_KEY.QUICK_BOOKS:
                return <FormattedMessage
                    id="integration.integrations.open_credit_account"
                    defaultMessage="Open credit account deposit in {integrationKey}"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            case INTEGRATION_KEY.STRIPE:
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                return <FormattedMessage
                    id="integration.integrations.open_payment_from"
                    defaultMessage="Open payment from {integrationKey}"
                    values={{ integrationKey: Integrations.getIntegrationNameFromKey(integrationKey) }}
                />
            default:
                return ''
        }
    }

    static getExternalOrderUrl(externalOrder) {
        let url = ''
        switch ( externalOrder.attributes.integrationKey ) {
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                url = 'https://us.merchantos.com/?name=workbench.views.beta_workorder&form_name=view&tab=details&id=' + externalOrder.attributes.externalId
                break
            case INTEGRATION_KEY.ZENDESK_SELL:
                url = 'https://app.futuresimple.com/sales/deals/' + externalOrder.attributes.externalId
                break
            case INTEGRATION_KEY.ZENDESK_SUPPORT:
                let subdomain = AppContext.getIntegrationExternalAccountId(INTEGRATION_KEY.ZENDESK_SUPPORT)
                url = `https://${subdomain}.zendesk.com/agent/tickets/` + externalOrder.attributes.externalId
                break
            case INTEGRATION_KEY.VEND:
                let accountId = AppContext.getIntegrationExternalAccountId(INTEGRATION_KEY.VEND)
                url = `https://${accountId}.retail.lightspeed.app/sales/all?ids=${externalOrder.attributes.externalId}`
                break
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                if (GlobalHelper.isEnvProduction()) {
                    url = `https://squareup.com/dashboard/orders/overview/${externalOrder.attributes.externalId}`
                } else {
                    url = `https://squareupsandbox.com/dashboard/orders/overview/${externalOrder.attributes.externalId}`
                }
                break
            case INTEGRATION_KEY.PIPEDRIVE:
                const firstPipedriveIntegrationFound = AppContext.integrations
                    .find(it => it.attributes.integrationKey === INTEGRATION_KEY.PIPEDRIVE)
                if (!firstPipedriveIntegrationFound) {
                    return ''
                }
                const companyDomain = firstPipedriveIntegrationFound.attributes.configuration.companyDomain
                return `https://${companyDomain}.pipedrive.com/deal/${externalOrder.attributes.externalId}`
            case INTEGRATION_KEY.HUBSPOT:
                const firstHubSpotIntegrationFound = AppContext.integrations
                    .find(it => it.attributes.integrationKey === INTEGRATION_KEY.HUBSPOT)
                if (!firstHubSpotIntegrationFound) {
                    return ''
                }
                const externalAccountId = firstHubSpotIntegrationFound.attributes.externalAccountId
                return `https://app.hubspot.com/contacts/${externalAccountId}/record/0-3/${externalOrder.attributes.externalId}`
            case INTEGRATION_KEY.MAILCHIMP:
                const integrationMailchimp = GlobalHelper.getIntegration(INTEGRATION_KEY.MAILCHIMP)
                if (integrationMailchimp) {
                    return `https://${integration.attributes.dc ?? "us9"}.admin.mailchimp.com/audience/contact-profile?contact_id=${externalOrder.attributes.metadata.contactExternalId}`
                }
            case INTEGRATION_KEY.FRESHBOOKS:
                const integrationFreshbooks = GlobalHelper.getIntegration(INTEGRATION_KEY.FRESHBOOKS)
                if (integrationFreshbooks) {
                    return `https://my.freshbooks.com/#/invoice/${integrationFreshbooks.attributes.externalAccountId}-${externalOrder.attributes.externalId}`
                }
            case INTEGRATION_KEY.WIX:
                const wixIntegration = GlobalHelper.getIntegration(INTEGRATION_KEY.WIX)
                if (wixIntegration) {
                    return `https://manage.wix.com/dashboard/${wixIntegration.attributes.configuration.shopId}/store/orders/order/${externalOrder.attributes.externalId}`
                }
        }
        return url
    }

    static getShortenedPONumber(externalOrder) {
        let poNumber = externalOrder.attributes.externalId
        switch (externalOrder.attributes.integrationKey) {
            case INTEGRATION_KEY.VEND:
                let lastChars = poNumber.slice(poNumber.lastIndexOf('-') + 1);
                poNumber = lastChars.substr(6);
                break
            case integrationKeyQuickbooks:
                const docNumber = externalOrder.attributes.metadata.identifier
                return docNumber ?? poNumber
            default:
                return poNumber
        }
        return poNumber
    }

    static getExternalPurchaseOrderUrl(externalOrder) {
        let url = ''
        switch (externalOrder.attributes.integrationKey) {
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                url = 'https://us.merchantos.com/?name=purchase.views.purchase&form_name=view&tab=main&id=' + externalOrder.attributes.externalId
                break
            case INTEGRATION_KEY.VEND:
                const vendDomainName = AppContext.integrations.find(i => i.attributes.integrationKey === INTEGRATION_KEY.VEND)?.attributes?.configuration?.accountId
                url = `https://${vendDomainName}.retail.lightspeed.app/consignment/` + externalOrder?.attributes?.externalId
                break
            case INTEGRATION_KEY.QUICK_BOOKS:
                url = `https://app.sandbox.qbo.intuit.com/app/purchaseorder?txnId=${externalOrder.attributes.externalId}`
                if (GlobalHelper.isEnvProduction()) {
                    url = `https://qbo.intuit.com/app/purchaseorder?txnId=${externalOrder.attributes.externalId}`
                }
                break
        }
        return url
    }

    static getQuoteExternalUrl(externalLink) {
        let url = ''
        switch ( externalLink.attributes.integrationKey ) {
            case INTEGRATION_KEY.WIX:
                url = ""
                break
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                url = 'https://us.merchantos.com/?name=invoice.views.invoice&form_name=view&tab=details&id=' + externalLink.attributes.externalId
                break
        }
        return url
    }

    static getContactExternalUrl(externalLink) {
        let url = ''
        switch ( externalLink.attributes.integrationKey ) {
            case INTEGRATION_KEY.WIX:
                const wixIntegration = GlobalHelper.getIntegration(INTEGRATION_KEY.WIX)
                url = `https://manage.wix.com/dashboard/${wixIntegration.attributes.configuration.shopId}/contacts/view/${externalLink.attributes.externalId}?referralInfo=contacts`
                break
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                url = 'https://us.merchantos.com/?name=customer.views.customer&form_name=view&tab=details&id=' + externalLink.attributes.externalId
                break
            case INTEGRATION_KEY.VEND:
                let accountId = AppContext.getIntegrationExternalAccountId(INTEGRATION_KEY.VEND)
                url = `https://${accountId}.retail.lightspeed.app/customers/?direction=ASC&field=DEFAULT&ids=${externalLink.attributes.externalId}`
                break
            case INTEGRATION_KEY.QUICK_BOOKS:
                url = 'https://sandbox.qbo.intuit.com/app/customerdetail?nameId=' + externalLink.attributes.externalId
                if (GlobalHelper.isEnvProduction()) {
                    url = 'https://qbo.intuit.com/app/customerdetail?nameId=' + externalLink.attributes.externalId
                }
        }
        return url
    }

    static getProductExternalUrl(externalLink) {
        let url = ''
        const integrationKey = externalLink?.attributes?.integrationKey
        switch ( integrationKey ) {
            case INTEGRATION_KEY.WIX:
                url = ""
                break
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                url = 'https://us.merchantos.com/?name=item.views.item&form_name=view&tab=details&id=' + externalLink.attributes.externalId
                break
        }
        return url
    }

    static getIntegrationDetails(integrationKey) {
        return details.filter(d => d.key === integrationKey)[0] || {}
    }

    static getIntegrationNameFromKey(integrationKey) {
        const label = INTEGRATION_LABEL[integrationKey]
        if (label) {
            return label
        }
        const integration = this.getIntegrationDetails(integrationKey)
        return integration.name || integrationKey
    }

    static getIntegrationLogoImagePath(integrationKey, useAlternativeLogo = false) {
        switch ( integrationKey ) {
            case INTEGRATION_KEY.WIX:
                return "/images/wix_logo.png"
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                if (useAlternativeLogo) {
                    return "/images/lightspeed_flame_logo.png"
                }
                return "/images/lightspeed_retail_logo.png"
            case INTEGRATION_KEY.VEND:
                if (useAlternativeLogo) {
                    return "/images/lightspeed_flame_logo.png"
                }
                return "/images/lightspeed_logo.png"
            case INTEGRATION_KEY.LIGHTSPEED_ECOM:
            case INTEGRATION_KEY.LIGHTSPEED_ECOM_OMNI:
            case INTEGRATION_KEY.LIGHTSPEED_ECOM_OMNI_NA:
                return "/images/lightspeed_ecom_logo.png"
            case INTEGRATION_KEY.QUICK_BOOKS:
                if (useAlternativeLogo) {
                    return "/images/quickbooks_btn.png"
                }
                return "/images/quickbooks_logo.png"
            case INTEGRATION_KEY.STRIPE:
                return "/images/stripe_logo.png"
            case INTEGRATION_KEY.KANGAROO:
                return "/images/kangaroo_logo.png"
            case INTEGRATION_KEY.PAYSAFE:
                return "/images/paysafe_logo.jpg"
            case INTEGRATION_KEY.NYLAS:
                return "/images/nylas_logo.jpg"
            case INTEGRATION_KEY.SHOPIFY_EXPERT_APP:
            case INTEGRATION_KEY.SHOPIFY:
                return "/images/shopify_logo.png"
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                return "/images/square_logo.png"
            case INTEGRATION_KEY.PIPEDRIVE:
                return "/images/pipedrive_logo.png"
            case INTEGRATION_KEY.HUBSPOT:
                return "/images/hubspot_logo.svg"
            case INTEGRATION_KEY.MAILCHIMP:
                return "/images/mailchimp_logo_cropped.png"
            case INTEGRATION_KEY.ZENDESK_SUPPORT:
                return "/images/zendesk_support_logo.png"
            case INTEGRATION_KEY.FRESHBOOKS:
                return "/images/freshbooks_logo.png"
            default:
                return ''
        }
    }

    static getIntegrationLogoImage(integrationKey, width, height) {
        switch ( integrationKey ) {
            case INTEGRATION_KEY.WIX:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Wix" />
            case INTEGRATION_KEY.LIGHTSPEED:
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Lightspeed" />
            case INTEGRATION_KEY.LIGHTSPEED_ECOM:
            case INTEGRATION_KEY.LIGHTSPEED_ECOM_OMNI:
            case INTEGRATION_KEY.LIGHTSPEED_ECOM_OMNI_NA:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Lightspeed eCom" />
            case INTEGRATION_KEY.QUICK_BOOKS:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Quickbooks" />
            case INTEGRATION_KEY.STRIPE:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Stripe" />
            case INTEGRATION_KEY.KANGAROO:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Kangaroo" />
            case INTEGRATION_KEY.PAYSAFE:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Paysafe" />
            case INTEGRATION_KEY.NYLAS:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Nylas" />
            case INTEGRATION_KEY.SHOPIFY:
            case INTEGRATION_KEY.SHOPIFY_EXPERT_APP:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Shopify" />
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Square" />
            case INTEGRATION_KEY.VEND:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Vend" />
            case INTEGRATION_KEY.PIPEDRIVE:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Pipedrive" />
            case INTEGRATION_KEY.HUBSPOT:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="HubSpot" />
            case INTEGRATION_KEY.MAILCHIMP:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Mailchimp" />
            case INTEGRATION_KEY.ZENDESK_SUPPORT:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="Zendesk Support" />
            case INTEGRATION_KEY.FRESHBOOKS:
                return <img src={this.getIntegrationLogoImagePath(integrationKey)} width={width} height={height} alt="FreshBooks" />
            default:
                return ''
        }
    }

    static getIntegrationIcon(integrationkey, width, height) {
        switch ( integrationkey ) {
            case INTEGRATION_KEY.WIX:
                return <img src="/images/wix_logo.png" width={width} alt="Wix" />
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
            case INTEGRATION_KEY.LIGHTSPEED:
            case INTEGRATION_KEY.VEND:
                return <img src="/images/lightspeed_flame_logo.png" width={width} alt="Lightspeed" />
            case INTEGRATION_KEY.SHOPIFY:
            case INTEGRATION_KEY.SHOPIFY_EXPERT_APP:
                return <img src="/images/shopify_bag_logo.png" width={width} alt="Shopify" />
            case INTEGRATION_KEY.SQUARE:
            case INTEGRATION_KEY.SQUARE_PAYMENTS:
                return <img src="/images/square_icon_logo.png" width={width} alt="Square" />
            case INTEGRATION_KEY.QUICK_BOOKS:
                return <img src="/images/quickbooks_btn.png" width={width} alt="Quickbooks" />
            case INTEGRATION_KEY.ZENDESK_SUPPORT:
                return <img src="/images/zendesk_support_btn.png" width={width} alt="Zendesk Support" />
            default:
                return ''
        }
    }

    static isIntegrationAuthConnect(integrationKey) {
        const integration = this.getIntegrationDetails(integrationKey)
        return integration.authConnect
    }

    static getExternalSaleUrl(importableDto) {
        switch (importableDto.integrationKey) {
            case INTEGRATION_KEY.LIGHTSPEED_RETAIL:
                return `https://us.merchantos.com/?name=transaction.views.transaction&form_name=view&tab=details&id=${importableDto.saleId}`
            default:
                return ''
        }
    }

    static getAvailableFilters() {
        return AppContext.integrations
            .filter(elem =>
                this.getIntegrationDetails(elem.attributes.integrationKey).hasFilter &&
                elem.attributes.enabled
            )
            .map(integration => {
                const integrationWithDetails = this.getIntegrationDetails(integration.attributes.integrationKey)
                return ({ value: integrationWithDetails.key, label: Integrations.getIntegrationNameFromKey(integrationWithDetails.key) })
            })
    }

    static getAvailableSuperUserFiltersWithSupportedFeature(supportedFeature) {
        return GlobalHelper.getIntegrationsWithSupportedFeature(supportedFeature)
            .filter(elem =>
                this.getIntegrationDetails(elem.attributes.integrationKey).hasSuperUserFilter
            )
            .map(integration => {
                const key = integration.attributes.integrationKey
                const externalID = integration.attributes.externalAccountId
                return ({
                    value: key,
                    label: `${key}-${externalID}`,
                    metadata: {
                        integration: integration
                    }
                })
            })
    }

    static getIntegrationSignUpRedirectUrl(integrationKey) {
        switch (integrationKey) {
            case INTEGRATION_KEY.SHOPIFY:
                return shopifyExternalUrl
            default:
                return AppContext.getOptions().apiUrl + `/openid/signup/${integrationKey}`
        }
    }


}

export default Integrations


// WEBPACK FOOTER //
// ./src/integration/Integrations.jsx